import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faTriangleExclamation,
  faSpinner,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import { SupplierFactorPropagationState } from 'modules/FreeSupplier/supplierFactorContracts';

// The "Sync" cell of the emissions table: how far a published/corrected factor
// has propagated to the declarants consuming it. Propagation is async and not
// pushed (D-11), so this reflects the latest snapshot the list refetch carried —
// "Publishing…" may linger briefly. Copy per D40 (published → syncing / synced /
// degraded). Per-handler detail is exposed on hover via the title tooltip.
//
// Module-scope (not exported/reused elsewhere) — built as functions of `t`,
// called via useMemo / passed `t` directly below.
const buildHandlerLabels = (t) => ({
  shipmentSnapshots: t('freeSupplier:propagationStatus.handlerShipments', 'Shipments'),
  lots: t('freeSupplier:propagationStatus.handlerLots', 'Lots'),
  cbamProducts: t('freeSupplier:propagationStatus.handlerProducts', 'Products'),
  diagramPrecursors: t(
    'freeSupplier:propagationStatus.handlerDiagramPrecursors',
    'Diagram precursors',
  ),
  dataRequestFulfilment: t('freeSupplier:propagationStatus.handlerDataRequests', 'Data requests'),
});

const buildStateCopy = (t) =>
  Object.freeze({
    [SupplierFactorPropagationState.Queued]: {
      label: t('freeSupplier:propagationStatus.publishing', 'Publishing…'),
      tone: 'text-slate-500 bg-slate-100',
      icon: faSpinner,
      spin: true,
    },
    [SupplierFactorPropagationState.Processing]: {
      label: t('freeSupplier:propagationStatus.publishing', 'Publishing…'),
      tone: 'text-slate-500 bg-slate-100',
      icon: faSpinner,
      spin: true,
    },
    [SupplierFactorPropagationState.Complete]: {
      label: t('freeSupplier:propagationStatus.synced', 'Synced'),
      tone: 'text-emerald-700 bg-emerald-50',
      icon: faCircleCheck,
    },
    [SupplierFactorPropagationState.Degraded]: {
      label: t('freeSupplier:propagationStatus.syncDegraded', 'Sync degraded'),
      tone: 'text-amber-700 bg-amber-50',
      icon: faTriangleExclamation,
    },
  });

const buildUnknownCopy = (t) => ({
  label: t('freeSupplier:propagationStatus.syncStatusUnknown', 'Sync status unknown'),
  tone: 'text-slate-400 bg-slate-100',
  icon: faCircleQuestion,
});

const handlerSummary = (t, propagation) => {
  const handlerLabels = buildHandlerLabels(t);
  const handlers = propagation?.handlers || {};
  const lines = Object.entries(handlerLabels).map(([key, label]) => {
    const handler = handlers[key];
    if (!handler) return `${label}: —`;
    const applied = handler.appliedCount
      ? t('freeSupplier:propagationStatus.appliedCountSuffix', ' ({{count}} applied)', {
          count: handler.appliedCount,
        })
      : '';
    return `${label}: ${handler.state}${applied}`;
  });
  if (propagation?.available === false) {
    lines.unshift(
      t(
        'freeSupplier:propagationStatus.recordUnavailable',
        'Propagation record unavailable — will retry.',
      ),
    );
  }
  return lines.join('\n');
};

export default function PropagationStatusBadge({ propagation }) {
  const { t } = useTranslation('freeSupplier');
  const stateCopy = useMemo(() => buildStateCopy(t), [t]);
  const unknownCopy = useMemo(() => buildUnknownCopy(t), [t]);

  if (!propagation) {
    return <span className="text-[11px] text-slate-300">—</span>;
  }
  const copy = stateCopy[propagation.state] || unknownCopy;
  const retryHint =
    propagation.state === SupplierFactorPropagationState.Degraded && propagation.retryable
      ? t('propagationStatus.retryableSuffix', ' — retryable')
      : '';

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap ${copy.tone}`}
      title={handlerSummary(t, propagation)}
    >
      <FontAwesomeIcon
        icon={copy.icon}
        className={copy.spin ? 'animate-spin' : undefined}
      />
      {copy.label}
      {retryHint}
    </span>
  );
}
